import React, { useState, useRef } from 'react'
import {
  ExpressCheckoutElement,
  PaymentElement,
  Elements,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { useContext } from 'react'
import { ProductBusketContext } from '../../layouts/ProductLayout'
import { GlobalContext } from '../../appContext/GlobalContext'
import { getTotalCostLocationBased } from '../../utils/price'
import { Button } from '../../common'
import { toast } from 'react-hot-toast'
import { formatPrice } from '../../utils/number'
import { useLocation, useNavigate } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import { RxCross2 } from 'react-icons/rx'
import { useTranslation } from 'react-i18next'
import convertTimeStringToDateISO from '../../utils/convertTimeStringToDateISO'
import usePaymentState from '../../hooks/usePaymentState'

const configKey = process.env.REACT_APP_PUBLISHABLE_KEY
const shouldLogStripeDebug = process.env.NODE_ENV !== 'production'

const getPaymentIntentFailureMessage = (paymentIntentResponse) => {
  return (
    paymentIntentResponse?.stripeMessage ||
    paymentIntentResponse?.message ||
    'Unable to initialize card payment. Please try again.'
  )
}

const logPaymentIntentDebug = (label, payload) => {
  if (shouldLogStripeDebug) {
    console.log(label, payload)
  }
}

// ----------------------------------------------------------------------
// NEW: Helper to compute total charge including Stripe and adhoc fees
// All amounts are in base currency (RON, USD, etc.) – not cents
// ----------------------------------------------------------------------
const computeCharges = ({
  netAmount,
  stripeRate,
  stripeFixed,
  adhocFee,
}) => {
  stripeRate = parseFloat(stripeRate)
  stripeFixed = parseFloat(stripeFixed)
  adhocFee = parseFloat(adhocFee)

  // Total charge before any rounding
  const totalCharge = (netAmount + stripeFixed + adhocFee) / (1 - stripeRate)

  // Stripe fee = percentage of totalCharge + fixed fee
  const stripeFee = stripeRate * totalCharge + stripeFixed

  return {
    totalCharge,          // customer pays this (raw value)
    stripeFee,            // Stripe's fee (raw value)
    adhocFee,             // your platform fee
    netToMerchant: totalCharge - stripeFee - adhocFee,
  }
}

// ----------------------------------------------------------------------
// CheckoutForm now receives totalCharge, adhocFee, and sharedPaymentState
// ----------------------------------------------------------------------
export const CheckoutForm = ({ stripeConnectedAccount, discountAmount, promoCodeDiscount, totalCharge, adhocFee, stripeFee, sharedPaymentState }) => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const stripe = useStripe()
  const elements = useElements()
  const { busket, getCompleteInvoice, tips, delivPickupType, deliveryData, setDeliveryData, pickupData, setPickupData } = useContext(ProductBusketContext)
  const { colorCode, location } = useContext(GlobalContext)
  const [isLoading, setIsLoading] = useState(false)
  const [email, setEmail] = useState()
  const [customerName, setCustomerName] = useState('')

  let old_busket = localStorage.getItem('merged_busket');
  if (old_busket) {
    old_busket = JSON.parse(old_busket)
  }

  const productTotal = getTotalCostLocationBased(old_busket ? old_busket : busket, location.LocationId)

  const validEmailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/

  const { state } = useLocation();
  const { pay_later_payment } = state || {};

  const handleChangeEmail = (e) => {
    setEmail(e.target.value)
    setDeliveryData(prev => ({
      ...prev,
      email: e.target.value
    }))
  }

  const handleChangeName = (e) => {
    setCustomerName(e.target.value)
    setDeliveryData(prev => ({
      ...prev,
      firstName: e.target.value
    }))
    setPickupData(prev => ({
      ...prev,
      firstName: e.target.value
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (sharedPaymentState.hasPaymentInProgress()) {
      toast.error('Payment already in progress. Please wait.', {
        position: 'bottom-right',
      })
      return
    }

    if (!email || !email.match(validEmailRegex)) {
      toast.error('Email is not valid', {
        position: 'bottom-right',
      })
      return
    }

    if (!stripe || !elements) {
      return
    }

    const paymentStarted = sharedPaymentState.startPayment('card')
    if (!paymentStarted) {
      toast.error('Payment already in progress. Please wait.', {
        position: 'bottom-right',
      })
      return
    }

    setIsLoading(true)

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit()
    if (submitError) {
      toast.error(submitError.message, {
        position: 'bottom-right',
      })
      setIsLoading(false)
      sharedPaymentState.completePayment('card')
      return
    }

    toast.loading('We are processing, please wait a bit..', {
      position: 'bottom-right',
      duration: 2000,
    })

    // Use the pre‑computed totalCharge (customer pays this) and adhocFee
    const amountInCents = Math.round(totalCharge * 100)
    const platformFeeInCents = Math.round(adhocFee * 100)
    const stripeFeeInCents = Math.round(stripeFee * 100)

    let clientSecret
    let paymentIntentId

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/order/create/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amountInCents,
          stripeConnectedAccount: stripeConnectedAccount,
          platformFee: platformFeeInCents,
          stripeFee: stripeFeeInCents,
        }),
      })

      const paymentIntentResponse = await res.json()
      clientSecret = paymentIntentResponse?.clientSecret
      paymentIntentId = paymentIntentResponse?.paymentIntentId

      if (!res.ok || paymentIntentResponse?.error || !clientSecret) {
        logPaymentIntentDebug('Stripe PaymentIntent creation failed', {
          responseOk: res.ok,
          response: paymentIntentResponse,
          stripeScope: stripeConnectedAccount ? 'connected_account_available' : 'platform_only',
        })
        toast.error(getPaymentIntentFailureMessage(paymentIntentResponse), {
          position: 'bottom-right',
        })
        setIsLoading(false)
        sharedPaymentState.completePayment('card')
        return
      }
    } catch (err) {
      logPaymentIntentDebug('Stripe PaymentIntent request crashed', {
        message: err.message,
      })
      toast.error('Unable to initialize card payment. Please try again.', {
        position: 'bottom-right',
      })
      setIsLoading(false)
      sharedPaymentState.completePayment('card')
      return
    }

    const { error } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url: 'https://example.com',
        payment_method_data: {
          billing_details: {
            email: email,
            name: customerName,
          },
        },
      },
      redirect: 'if_required',
    })

    if (error) {
      toast.error(error.message, {
        position: 'bottom-right',
      })
      setIsLoading(false)
      sharedPaymentState.completePayment('card')
      return
    } else {
      toast.loading('Almost there..', {
        position: 'bottom-right',
        duration: 2000,
      })

      if (pay_later_payment) {
        let currentPaylaterOrder = JSON.parse(localStorage.getItem('currentPaylaterOrder'))
        let tips = JSON.parse(localStorage.getItem('tips'))

        const dataBody = {
          orderId: currentPaylaterOrder['order_id'],
          ...getCompleteInvoice(),
          req_mode: 'update_and_pay',
          paymentIntentId,
          email,
          tips: formatPrice(tips),
          discounts_products: productTotal['discountedTotalPrice'] > 0 ? (productTotal['result'] - productTotal['discountedTotalPrice']) : 0,
          stripeConnectedAccount,
          discount_using_promo: discountAmount,
          promo_code_applied: promoCodeDiscount,
        }
        dataBody['order']['items'] = []
        setIsLoading(true)
        fetch(`${process.env.REACT_APP_API_URL}/order/create/paylater_order`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dataBody),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data['orderId']) {
              navigate('/food-ordering/thankYou', {
                state: {
                  location: `${location?.LocationId}`,
                  pay_method: 'pay_later',
                  pay_later_status: 'payment_completed'
                },
              })
            } else {
              throw Error(`Some error occured`)
            }
          })
          .catch((err) => {
            toast.error(err.message, {
              position: 'bottom-right',
            })
            navigate('/food-ordering/payment-failed',
              {
                state: {
                  location: `${location?.LocationId}`
                }
              })
          })
          .finally(() => {
            setIsLoading(false)
            sharedPaymentState.completePayment('card')
          })
      } else {
        const dataBody = delivPickupType == 'pickup' ? {
          ...getCompleteInvoice(),
          paymentIntentId,
          email,
          tips: formatPrice(tips),
          discounts_products: productTotal['discountedTotalPrice'] > 0 ? (productTotal['result'] - productTotal['discountedTotalPrice']) : 0,
          discount_using_promo: discountAmount,
          promo_code_applied: promoCodeDiscount,
          stripeConnectedAccount,
          type: "pickup",
          deliveryDate: convertTimeStringToDateISO(`${pickupData.day}, ${pickupData.time}`),
          delivery_phone: pickupData.phone,
          delivery_email: email,
          delivery_firstName: pickupData.firstName.split(' ')[0] ?? '',
          delivery_lastName: pickupData.firstName.split(' ')[1] ?? '',
        }
          : delivPickupType == 'delivery' ? {
            ...getCompleteInvoice(),
            paymentIntentId,
            email,
            tips: formatPrice(tips),
            discounts: (productTotal['result'] - productTotal['discountedTotalPrice']),
            stripeConnectedAccount,
            type: "delivery",
            deliveryAddress: {
              "postalCode": deliveryData.postalCode.toString(),
              "street": deliveryData.street,
              "city": deliveryData.city,
              "county": deliveryData.county,
              "long": deliveryData.long,
              "lat": deliveryData.lat,
              "country": deliveryData.country,
              "isDeliveryAddress": true,
              "isInvoiceAddress": true,
              "details": deliveryData.details
            },
            "noteForCourier": deliveryData.noteForCourier,
            "delivery_firstName": deliveryData.firstName,
            "delivery_lastName": deliveryData.lastName,
            "delivery_phone": deliveryData.phone.toString(),
            "delivery_email": deliveryData.email,
          }
            : {
              ...getCompleteInvoice(),
              paymentIntentId,
              email,
              tips: formatPrice(tips),
              discounts_products: productTotal['discountedTotalPrice'] > 0 ? (productTotal['result'] - productTotal['discountedTotalPrice']) : 0,
              stripeConnectedAccount,
              discount_using_promo: discountAmount,
              promo_code_applied: promoCodeDiscount,
            }
        fetch(`${process.env.REACT_APP_API_URL}/order/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dataBody),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data['success']) {
              if (delivPickupType == 'pickup' && (data?.orderNo || data?.paymentIntent?.newInvoiceNumber)) {
                if (data?.orderNo) window.localStorage.setItem("thankyou_code", data.orderNo)
                else if (data?.paymentIntent?.newInvoiceNumber) window.localStorage.setItem("thankyou_code", data?.paymentIntent?.newInvoiceNumber)
              }
              navigate(
                '/food-ordering/thankYou',
                {
                  state: {
                    location: `${location?.LocationId}`,
                    discountAmount: discountAmount,
                    promoCodeDiscount: promoCodeDiscount
                  }
                }
              )
            } else {
              throw Error(`Some error occured`)
            }
          }).catch(err => {
            toast.error(err.message, {
              position: 'bottom-right',
            })
            navigate('/food-ordering/payment-failed',
              {
                state: {
                  location: `${location?.LocationId}`
                }
              })
          }).finally(() => {
            setIsLoading(false)
            sharedPaymentState.completePayment('card')
          })
      }
    }
  }

  const paymentElementOptions = {
    layout: 'tabs',
    paymentMethodTypes: ['card']
  }

  const isWalletPaymentInProgress =
    sharedPaymentState.isPaymentInProgress &&
    sharedPaymentState.activePaymentMethod === 'wallet'

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <div>
        <h4 className="font-normal mt-[5px] text-[16px]">
          {t('product.owners_email')}
        </h4>
        <input
          className="font-normal shadow border border-[#F9F9F9] rounded-[18px] text-[16px] bg-white text-black py-2 px-4 transition duration-200 ease-in-out outline-none w-full"
          style={{
            fontFamily: 'sans-serif !important',
          }}
          required
          type="email"
          name="cardHolderEmail"
          placeholder="john.doe@adhocorder.ro"
          onChange={(e) => handleChangeEmail(e)}
          disabled={isWalletPaymentInProgress}
        />
      </div>
      <div>
        <h4 className="font-normal mt-[5px] text-[16px]">
          {t('product.holders_name')}
        </h4>
        <input
          className="font-normal shadow border border-[#F9F9F9] mb-[5px] text-[16px] rounded-[18px] bg-white text-black py-2 px-4 transition duration-200 ease-in-out outline-none w-full"
          style={{
            fontFamily: 'sans-serif !important',
          }}
          required
          name="cardHolderName"
          placeholder={t('product.cardHolder')}
          onChange={(e) => handleChangeName(e)}
          disabled={isWalletPaymentInProgress}
        />
      </div>
      <PaymentElement id="payment-element" options={paymentElementOptions} />
      <Button
        id="submit"
        colorCode={colorCode}
        className="w-full mt-2 font-medium"
        type="submit"
        text={isLoading ? `${t('product.order_sent')} ...` : t('product.confirm_payment')}
        disabled={isLoading || !stripe || !elements || isWalletPaymentInProgress}
      />
      {isWalletPaymentInProgress && (
        <p className="mt-2 text-[14px] sm:text-center font-medium text-orange-600">
          Wallet payment is processing...
        </p>
      )}
      <p className="mt-2 text-[14px] sm:text-center font-medium">
        {t('product.payment_notes')}
      </p>
      <p className="mt-2 text-[14px] sm:text-center font-medium">
        {t('product.need_assist')}{' '}
        <a
          className="cursor-pointer text-[14px]"
          style={{ color: colorCode }}
          href="https://adhocorder.ro/help-and-support"
        >
          {t('product.contact_us')}
        </a>
      </p>
    </form>
  )
}

// ----------------------------------------------------------------------
// ExpressCheckoutPage – with shared payment state locking
// ----------------------------------------------------------------------
export const ExpressCheckoutPage = ({ stripeConnectedAccount, discountAmount, promoCodeDiscount, totalCharge, adhocFee, stripeFee, sharedPaymentState }) => {
  const navigate = useNavigate()
  const stripe = useStripe()
  const elements = useElements()
  const { busket, getCompleteInvoice, tips, delivPickupType, deliveryData, pickupData } = useContext(ProductBusketContext)
  const { location } = useContext(GlobalContext)

  let old_busket = localStorage.getItem('merged_busket');
  if (old_busket) {
    old_busket = JSON.parse(old_busket)
  }

  const productTotal = getTotalCostLocationBased(old_busket ? old_busket : busket, location.LocationId)

  const [isLoading, setIsLoading] = useState(false)

  const { state } = useLocation();
  const { pay_later_payment } = state || {};

  const onConfirm = async (e) => {
    if (!stripe || !elements) {
      sharedPaymentState.completePayment('wallet')
      return
    }

    if (!sharedPaymentState.startPayment('wallet')) {
      toast.error('Payment already in progress. Please wait.', {
        position: 'bottom-right',
      })
      e.paymentFailed({ reason: 'fail' })
      return
    }

    setIsLoading(true)
    const { error: submitError } = await elements.submit()
    if (submitError) {
      toast.error(submitError.message, {
        position: 'bottom-right',
      })
      setIsLoading(false)
      sharedPaymentState.completePayment('wallet')
      return
    }

    // Use the pre‑computed totalCharge and adhocFee
    const amountInCents = Math.round(totalCharge * 100)
    const platformFeeInCents = Math.round(adhocFee * 100)
    const stripeFeeInCents = Math.round(stripeFee * 100)

    let clientSecret
    let paymentIntentId

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/order/create/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amountInCents,
          stripeConnectedAccount: stripeConnectedAccount,
          platformFee: platformFeeInCents,
          stripeFee: stripeFeeInCents,
        }),
      })
      const paymentIntentResponse = await res.json()
      clientSecret = paymentIntentResponse?.clientSecret
      paymentIntentId = paymentIntentResponse?.paymentIntentId

      if (!res.ok || paymentIntentResponse?.error || !clientSecret) {
        logPaymentIntentDebug('Stripe PaymentIntent creation failed', {
          responseOk: res.ok,
          response: paymentIntentResponse,
          stripeScope: stripeConnectedAccount ? 'connected_account_available' : 'platform_only',
        })
        toast.error(getPaymentIntentFailureMessage(paymentIntentResponse), {
          position: 'bottom-right',
        })
        setIsLoading(false)
        sharedPaymentState.completePayment('wallet')
        return
      }
    } catch (err) {
      logPaymentIntentDebug('Stripe PaymentIntent request crashed', {
        message: err.message,
      })
      toast.error('Unable to initialize wallet payment. Please try again.', {
        position: 'bottom-right',
      })
      setIsLoading(false)
      sharedPaymentState.completePayment('wallet')
      return
    }

    const { error } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url: 'https://example.com',
      },
      redirect: 'if_required',
    })

    if (error) {
      toast.error(error.message, {
        position: 'bottom-right',
      })
      setIsLoading(false)
      sharedPaymentState.completePayment('wallet')
      return
    } else {
      const email =
        window.localStorage.getItem('email') ?? e.billingDetails.email ?? ''

      if (pay_later_payment) {
        let currentPaylaterOrder = JSON.parse(localStorage.getItem('currentPaylaterOrder'))
        let tips = JSON.parse(localStorage.getItem('tips'))

        const dataBody = {
          orderId: currentPaylaterOrder['order_id'],
          ...getCompleteInvoice(),
          req_mode: 'update_and_pay',
          paymentIntentId,
          email,
          tips: formatPrice(tips),
          discounts_products: productTotal['discountedTotalPrice'] > 0 ? (productTotal['result'] - productTotal['discountedTotalPrice']) : 0,
          stripeConnectedAccount,
          discount_using_promo: discountAmount,
          promo_code_applied: promoCodeDiscount,
        }
        dataBody['order']['items'] = []
        setIsLoading(true)
        fetch(`${process.env.REACT_APP_API_URL}/order/create/paylater_order`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dataBody),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data['orderId']) {
              navigate('/food-ordering/thankYou', {
                state: {
                  location: `${location?.LocationId}`,
                  pay_method: 'pay_later',
                  pay_later_status: 'payment_completed'
                },
              })
            } else {
              throw Error(`Some error occured`)
            }
          })
          .catch((err) => {
            toast.error(err.message, {
              position: 'bottom-right',
            })
            navigate('/food-ordering/payment-failed',
              {
                state: {
                  location: `${location?.LocationId}`
                }
              })
          })
          .finally(() => {
            setIsLoading(false)
            sharedPaymentState.completePayment('wallet')
          })
      } else {
        const dataBody = delivPickupType == 'pickup' ? {
          ...getCompleteInvoice(),
          paymentIntentId,
          email,
          tips: formatPrice(tips),
          discounts_products: productTotal['discountedTotalPrice'] > 0 ? (productTotal['result'] - productTotal['discountedTotalPrice']) : 0,
          discount_using_promo: discountAmount,
          promo_code_applied: promoCodeDiscount,
          stripeConnectedAccount,
          type: "pickup",
          deliveryDate: convertTimeStringToDateISO(`${pickupData.day}, ${pickupData.time}`),
          delivery_phone: pickupData.phone,
          delivery_email: email,
          delivery_firstName: pickupData.firstName.split(' ')[0] ?? '',
          delivery_lastName: pickupData.firstName.split(' ')[1] ?? '',
        }
          : delivPickupType == 'delivery' ? {
            ...getCompleteInvoice(),
            paymentIntentId,
            email,
            tips: formatPrice(tips),
            discounts: (productTotal['result'] - productTotal['discountedTotalPrice']),
            stripeConnectedAccount,
            type: "delivery",
            deliveryAddress: {
              "postalCode": deliveryData.postalCode.toString(),
              "street": deliveryData.street,
              "city": deliveryData.city,
              "county": deliveryData.county,
              "long": deliveryData.long,
              "lat": deliveryData.lat,
              "country": deliveryData.country,
              "isDeliveryAddress": true,
              "isInvoiceAddress": true,
              "details": deliveryData.details
            },
            "noteForCourier": deliveryData.noteForCourier,
            "delivery_firstName": deliveryData.firstName,
            "delivery_lastName": deliveryData.lastName,
            "delivery_phone": deliveryData.phone.toString(),
            "delivery_email": deliveryData.email,
          }
            : {
              ...getCompleteInvoice(),
              paymentIntentId,
              email,
              discounts_products: productTotal['discountedTotalPrice'] > 0 ? (productTotal['result'] - productTotal['discountedTotalPrice']) : 0,
              tips: formatPrice(tips),
              stripeConnectedAccount,
              discount_using_promo: discountAmount,
              promo_code_applied: promoCodeDiscount,
            }
        fetch(`${process.env.REACT_APP_API_URL}/order/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dataBody),
        })
          .then((res) => res.json())
          .then((data) => {
            if (delivPickupType == 'pickup' && (data?.orderNo || data?.paymentIntent?.newInvoiceNumber)) {
              if (data?.orderNo) window.localStorage.setItem("thankyou_code", data.orderNo)
              else if (data?.paymentIntent?.newInvoiceNumber) window.localStorage.setItem("thankyou_code", data?.paymentIntent?.newInvoiceNumber)
            }
            navigate(
              '/food-ordering/thankYou',
              {
                state: {
                  location: `${location?.LocationId}`,
                  discountAmount: discountAmount,
                  promoCodeDiscount: promoCodeDiscount
                }
              }
            )
          }).catch(err => {
            toast.error(err.message, {
              position: 'bottom-right',
            })
            navigate('/food-ordering/payment-failed',
              {
                state: {
                  location: `${location?.LocationId}`
                }
              })
          }).finally(() => {
            setIsLoading(false)
            sharedPaymentState.completePayment('wallet')
          })
      }
    }
  }

  const onClick = ({ resolve }) => {
    if (!sharedPaymentState.startPayment('wallet')) {
      toast.error('Payment already in progress. Please wait.', {
        position: 'bottom-right',
      })
      return
    }

    const options = {
      emailRequired: true,
    }
    resolve(options)
  }

  const onCancel = () => {
    setIsLoading(false)
    sharedPaymentState.completePayment('wallet')
  }

  const options = {
    layout: {
      maxRows: 2,
      maxColumns: 2,
      overflow: 'auto',
    },
    buttonTheme: {
      applePay: 'white-outline',
      googlePay: 'white',
    },
    buttonType: {
      googlePay: 'plain',
      applePay: 'plain',
    },
  }

  const isCardPaymentInProgress =
    sharedPaymentState.isPaymentInProgress &&
    sharedPaymentState.activePaymentMethod === 'card'

  return (
    <>
      {isCardPaymentInProgress ? (
        <p className="mt-2 text-[14px] text-center font-medium text-orange-600">
          Card payment is processing... Please wait.
        </p>
      ) : (
        <>
          <div style={{ pointerEvents: isLoading ? 'none' : 'auto', opacity: isLoading ? 0.6 : 1 }}>
            <ExpressCheckoutElement
              options={options}
              onConfirm={onConfirm}
              onClick={onClick}
              onCancel={onCancel}
            />
          </div>
          {isLoading && (
            <p className="mt-2 text-[14px] text-center font-medium text-orange-600">
              Wallet payment is processing... Please wait.
            </p>
          )}
        </>
      )}
    </>
  )
}

// ----------------------------------------------------------------------
// Main Checkout Component – with unified payment state
// ----------------------------------------------------------------------
const Checkout = ({ setIsVisible, isVisible, discountAmount, promoCodeDiscount }) => {
  const { t } = useTranslation()
  const [creditCardMethod] = useState('Credit Card')
  const { busket, tips, delivPickupPrice } = useContext(ProductBusketContext)
  const {
    method,
    renderCheckout,
    stripeConnectedAccount,
    location,
    currency,
    adhocCommissionEnabled,
    adhocCommissionValue,
    stripeCommissionEnabled,
    stripeFixedCommission,
    stripePercentageCommission
  } = useContext(GlobalContext)

  const sharedPaymentState = usePaymentState()

  let old_busket = localStorage.getItem('merged_busket');
  if (old_busket) {
    old_busket = JSON.parse(old_busket)
  }

  const productTotal = getTotalCostLocationBased(old_busket ? old_busket : busket, location.LocationId)

  // ------------------------------------------------------------------
  // NEW: Calculate net amount (what you want to receive after fees)
  // ------------------------------------------------------------------
  const netAmount = (productTotal['discountedTotalPrice'] || productTotal['result']) + tips + delivPickupPrice - discountAmount

  // Retrieve fee settings from context
  const stripeRate = stripeCommissionEnabled ? stripePercentageCommission / 100 : 0
  const stripeFixed = stripeCommissionEnabled ? stripeFixedCommission : 0
  const adhocFee = adhocCommissionEnabled ? adhocCommissionValue : 0

  // Compute the total charge (customer pays this)
  const { totalCharge, stripeFee } = computeCharges({
    netAmount,
    stripeRate,
    stripeFixed,
    adhocFee,
  })

  const retainedFee = stripeFee + adhocFee

  // totalSum is replaced by totalCharge for display
  const totalSum = totalCharge

  const stripePromise = useRef(null)
  const { i18n } = useTranslation()
  const stripeOptions = { locale: i18n.language }

  if (stripeConnectedAccount == null || renderCheckout === false) {
    toast.error('We are unable right now to accept online payments', {
      position: 'bottom-right',
    })
    return
  }
  if (
    stripeConnectedAccount &&
    stripeConnectedAccount !== '' &&
    stripeConnectedAccount !== 'undefined' &&
    retainedFee <= 0
  ) {
    stripeOptions.stripeAccount = stripeConnectedAccount
  }
  if (stripePromise.current == null) {
    loadStripe(configKey, stripeOptions)
      .then(res => stripePromise.current = res)
      .catch(error => {
        console.log('error loadStripe:', error.message)
      })
  }

  const rawAmount = Number(formatPrice(totalSum)) * 100

  if (Math.round(rawAmount) < 200) return // don't render if price is less then 2 RON

  const roundedAmount = Math.round(rawAmount)

  const elFonts = [
    { cssSrc: 'https://fonts.googleapis.com/css?family=Poppins:400' },
  ]

  const appearance = {
    theme: 'stripe',
    variables: {
      borderRadius: '24px',
      fontFamily: 'Poppins',
      colorText: '#000000',
      colorTextPlaceholder: '#B0B0B0',
      fontSizeBase: '17px',
      fontWeightNormal: '400',
      spacingUnit: '3px',
    },
  }

  const options = {
    mode: 'payment',
    amount: roundedAmount,
    currency: currency.toLowerCase(),
    fonts: elFonts,
    appearance,
  }
  return (
    <div
      className={`fixed z-50 top-0 left-0 w-full h-full bg-[#00000091] overflow-auto transition-all ${isVisible
          ? 'opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none'
        }`}
      onClick={() => setIsVisible(false)}
    >
      <div
        className="absolute sm:w-[630px] w-full top-[50%] left-[50%] bg-white shadow-lg border p-[30px] md:p-[65px] md:pr-[95px] flex flex-col rounded-[12px]"
        onClick={(e) => e.stopPropagation()}
        style={{ transform: 'translate(-50%,-35%)' }}
      >
        <div className="flex justify-between items-center mb-2">
          <img
            alt="Stripe Logo"
            src="/assets/images/help-step-6.svg"
            className="w-[100px] h-[48px]"
          />
          <button
            className="absolute right-[10px] bg-[#FFF] transition duration-300 ease-in-out hover:bg-gray-100 hover:shadow-lg rounded-[50%] p-[10px]"
            onClick={() => setIsVisible(false)}
          >
            <RxCross2 style={{ color: '#000000' }} />
          </button>
        </div>

        <div className="flex justify-center items-center flex-col">
          <p className="font-semibold text-[18px]">
            {t('product.total_payment')}
          </p>
          <p className="font-semibold text-xl">
            {formatPrice(totalSum)} {options.currency.toUpperCase()}
            <br />
          </p>
        </div>

        {method === 'online' ? (
          <Elements stripe={stripePromise.current} options={options}>
            {creditCardMethod === 'Credit Card' ? (
              <>
                <ExpressCheckoutPage 
                  stripeConnectedAccount={stripeConnectedAccount} 
                  discountAmount={discountAmount} 
                  promoCodeDiscount={promoCodeDiscount} 
                  totalCharge={totalCharge} 
                  adhocFee={adhocFee} 
                  stripeFee={stripeFee}
                  sharedPaymentState={sharedPaymentState}
                />
                <CheckoutForm
                  stripeConnectedAccount={stripeConnectedAccount}
                  discountAmount={discountAmount}
                  promoCodeDiscount={promoCodeDiscount}
                  totalCharge={totalCharge}
                  adhocFee={adhocFee}
                  stripeFee={stripeFee}
                  sharedPaymentState={sharedPaymentState}
                />
              </>
            ) : (
              <></>
            )}
          </Elements>
        ) : null}
      </div>
    </div>
  )
}

export default Checkout
